'use client';

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';

declare global {
  interface Window {
    LogRocket?: {
      captureException: (error: Error, options?: Record<string, unknown>) => void;
    };
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: ((error: Error, errorInfo: ErrorInfo) => void) | undefined;
  retryCount?: number;
  maxRetries?: number;
  errorReportingService?: 'sentry' | 'logrocket' | 'custom';
  supportEmail?: string;
  supportPhone?: string;
  customErrorHandler?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRecovering: boolean;
}

/**
 * Enhanced error boundary component with advanced error handling, recovery mechanisms,
 * and comprehensive error reporting for production applications
 */
export class ErrorBoundary extends Component<Props, State> {
  private errorId: string;
  private retryTimeout?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRecovering: false,
    };
    this.errorId = this.generateErrorId();
  }

  /**
   * Generate unique error ID for tracking and debugging
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store error info for debugging
    this.setState({ errorInfo });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.errorId,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
    this.props.customErrorHandler?.(error, errorInfo);

    // Report to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportErrorToService(error, errorInfo);
    }

    // Attempt automatic recovery for certain types of errors
    this.attemptAutomaticRecovery(error);
  }

  /**
   * Report error to external error reporting service
   */
  private reportErrorToService(error: Error, errorInfo: ErrorInfo): void {
    const errorData = {
      errorId: this.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      retryCount: this.state.retryCount,
      environment: process.env.NODE_ENV,
    };

    switch (this.props.errorReportingService) {
      case 'sentry':
        this.reportToSentry(errorData);
        break;
      case 'logrocket':
        this.reportToLogRocket(errorData);
        break;
      case 'custom':
        this.reportToCustomService(errorData);
        break;
      default:
        // Default to custom endpoint
        this.reportToCustomService(errorData);
    }
  }

  private reportToSentry(errorData: Record<string, unknown>): void {
    Sentry.captureException(new Error(errorData.message as string), {
      extra: errorData,
      tags: { errorId: errorData.errorId as string },
    });
  }

  /**
   * Report to LogRocket (if available)
   */
  private reportToLogRocket(errorData: Record<string, unknown>): void {
    if (typeof window !== 'undefined' && window.LogRocket) {
      window.LogRocket.captureException(new Error(errorData.message as string), {
        extra: errorData,
      });
    }
  }

  /**
   * Report to custom error reporting endpoint
   * Note: Custom endpoint not implemented - logging to console instead
   */
  private reportToCustomService(errorData: Record<string, unknown>): void {
    // Log error data for debugging - custom endpoint not yet implemented
    console.error('[ErrorBoundary] Client error captured:', {
      errorId: errorData.errorId,
      message: errorData.message,
      url: errorData.url,
      timestamp: errorData.timestamp,
    });
  }

  /**
   * Attempt automatic recovery for certain types of errors
   */
  private attemptAutomaticRecovery(error: Error): void {
    const recoverableErrors = [
      'Network Error',
      'Failed to fetch',
      'ChunkLoadError',
      'Loading chunk failed',
    ];

    if (recoverableErrors.some(msg => error.message.includes(msg))) {
      this.setState({ isRecovering: true });

      // Wait a bit before attempting recovery
      this.retryTimeout = setTimeout(() => {
        this.retry();
      }, 2000);
    }
  }

  /**
   * Retry rendering the component
   */
  private retry = (): void => {
    const { maxRetries = 3 } = this.props;

    if (this.state.retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        isRecovering: false,
      }));

      // Generate new error ID for next error
      this.errorId = this.generateErrorId();
    }
  };

  /**
   * Force refresh the page
   */
  private forceRefresh = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  /**
   * Reset error boundary state
   */
  private reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRecovering: false,
    });
    this.errorId = this.generateErrorId();
  };

  override componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  override render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Enhanced default fallback UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl p-8 text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Something went wrong
            </h1>

            <p className="text-gray-600 mb-6 leading-relaxed">
              We&apos;re sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
            </p>

            {/* Error ID for Support */}
            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Error ID:</strong> {this.errorId}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Please include this ID when contacting support
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.retry}
                disabled={this.state.isRecovering}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {this.state.isRecovering ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Recovering...
                  </span>
                ) : (
                  `Try Again (${this.state.retryCount + 1}/${(this.props.maxRetries || 3) + 1})`
                )}
              </button>

              <button
                onClick={this.forceRefresh}
                className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 font-semibold"
              >
                Refresh Page
              </button>

              <button
                onClick={this.reset}
                className="w-full text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                Reset Error Boundary
              </button>
            </div>

            {/* Enhanced Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error ? <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 p-3 rounded-lg">
                  🔍 Error Details (Development)
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="bg-red-50 p-3 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-2">Error Message</h4>
                    <p className="text-sm text-red-700 font-mono">{this.state.error.message}</p>
                  </div>

                  {this.state.errorInfo ? <div className="bg-red-50 p-3 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Component Stack</h4>
                      <pre className="text-xs text-red-700 font-mono whitespace-pre-wrap overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div> : null}

                  <div className="bg-red-50 p-3 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-2">Error Stack</h4>
                    <pre className="text-xs text-red-700 font-mono whitespace-pre-wrap overflow-auto">
                      {this.state.error.stack}
                    </pre>
                  </div>
                </div>
              </details> : null}

            {/* Support Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-2">
                Need help? Contact our support team:
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center text-sm">
                <a
                  href={`mailto:${this.props.supportEmail || 'support@routeroyal.com'}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {this.props.supportEmail || 'support@routeroyal.com'}
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Enhanced hook for handling errors in functional components
 */
export function useErrorHandler() {
  return React.useCallback((error: Error, errorInfo?: ErrorInfo, context?: Record<string, unknown>) => {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by useErrorHandler:', {
        error: error.message,
        stack: error.stack,
        errorInfo,
        context,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      });
    }

    // In production, you might want to send this to an error reporting service
    // Example: Sentry.captureException(error, { extra: { errorInfo, context } });
  }, []);
}

/**
 * Enhanced higher-order component for wrapping components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: {
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    retryCount?: number;
    maxRetries?: number;
    errorReportingService?: 'sentry' | 'logrocket' | 'custom';
    customErrorHandler?: (error: Error, errorInfo: ErrorInfo) => void;
  } = {},
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...options}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Error boundary for specific sections/components
 */
export function SectionErrorBoundary({
  children,
  fallback,
  onError,
}: {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}) {
  return (
    <ErrorBoundary
      fallback={fallback}
      onError={onError}
      maxRetries={1}
    >
      {children}
    </ErrorBoundary>
  );
}
